import React, { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../ui/dialog";

const EMPTY_SHIFT = { shift_template_id: "", start_time: "09:00", end_time: "17:00", crosses_midnight: false, break_minutes: 0, role: "" };

/** Opens once both an employee and a date are selected (via click on an EligibilityEmployeeList
 * card + a ScheduleGrid date cell, or by dragging a card onto a cell) - this is the single,
 * fully keyboard/click-accessible path to create an assignment. Drag/drop (see
 * EligibilityEmployeeList/ScheduleGrid) is a progressive enhancement that lands here too. */
export const QuickAssignDialog = ({ open, onOpenChange, employeeName, date, templates, onSubmit }) => {
  const [doubleShiftMode, setDoubleShiftMode] = useState(false);
  const [form, setForm] = useState(EMPTY_SHIFT);
  const [secondForm, setSecondForm] = useState(EMPTY_SHIFT);
  const [justification, setJustification] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) { setDoubleShiftMode(false); setForm(EMPTY_SHIFT); setSecondForm(EMPTY_SHIFT); setJustification(""); }
  }, [open]);

  const applyTemplate = (setter, templateId) => {
    const tpl = templates.find((t) => t.id === templateId);
    setter((f) => ({ ...f, shift_template_id: templateId, start_time: tpl?.start_time || f.start_time, end_time: tpl?.end_time || f.end_time, crosses_midnight: tpl?.crosses_midnight || false }));
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      await onSubmit({ doubleShiftMode, form, secondForm, justification });
      onOpenChange(false);
    } catch (e) {
      // Error already surfaced via toast by the page-level action helper - keep the dialog open.
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(next) => !submitting && onOpenChange(next)}>
      <DialogContent className="max-w-lg" data-testid="quick-assign-dialog">
        <DialogHeader>
          <DialogTitle>Assign {employeeName} - {date}</DialogTitle>
          <DialogDescription>Assignments save immediately once submitted - there is no separate "save draft" step.</DialogDescription>
        </DialogHeader>

        <label className="flex items-center gap-1.5 text-xs font-semibold text-gray-500">
          <input data-testid="quick-assign-double-shift-toggle" type="checkbox" checked={doubleShiftMode} onChange={(e) => setDoubleShiftMode(e.target.checked)} /> Linked double shift
        </label>

        <p className="text-xs font-semibold text-gray-500 pt-1">{doubleShiftMode ? "First shift" : "Shift"}</p>
        <div className="grid grid-cols-2 gap-2">
          <select data-testid="quick-assign-template-select" value={form.shift_template_id} onChange={(e) => applyTemplate(setForm, e.target.value)} className="px-3 py-2 rounded-lg border border-gray-300 text-sm col-span-2">
            <option value="">Manual times (or pick a template)</option>
            {templates.map((t) => <option key={t.id} value={t.id}>{t.code}</option>)}
          </select>
          <input data-testid="quick-assign-start-time" required type="time" value={form.start_time} onChange={(e) => setForm((f) => ({ ...f, start_time: e.target.value }))} className="px-3 py-2 rounded-lg border border-gray-300 text-sm" />
          <input data-testid="quick-assign-end-time" required type="time" value={form.end_time} onChange={(e) => setForm((f) => ({ ...f, end_time: e.target.value }))} className="px-3 py-2 rounded-lg border border-gray-300 text-sm" />
          <input data-testid="quick-assign-break" type="number" min="0" placeholder="Break min" value={form.break_minutes} onChange={(e) => setForm((f) => ({ ...f, break_minutes: Number(e.target.value) }))} className="px-3 py-2 rounded-lg border border-gray-300 text-sm" />
          <input data-testid="quick-assign-role" placeholder="Role (optional)" value={form.role} onChange={(e) => setForm((f) => ({ ...f, role: e.target.value }))} className="px-3 py-2 rounded-lg border border-gray-300 text-sm" />
        </div>
        <label className="flex items-center gap-1.5 text-xs text-gray-500">
          <input type="checkbox" checked={form.crosses_midnight} onChange={(e) => setForm((f) => ({ ...f, crosses_midnight: e.target.checked }))} /> Crosses midnight
        </label>

        {doubleShiftMode && (
          <>
            <p className="text-xs font-semibold text-gray-500 pt-1">Second shift</p>
            <div className="grid grid-cols-2 gap-2">
              <select data-testid="quick-assign-second-template-select" value={secondForm.shift_template_id} onChange={(e) => applyTemplate(setSecondForm, e.target.value)} className="px-3 py-2 rounded-lg border border-gray-300 text-sm col-span-2">
                <option value="">Manual times (or pick a template)</option>
                {templates.map((t) => <option key={t.id} value={t.id}>{t.code}</option>)}
              </select>
              <input data-testid="quick-assign-second-start-time" required type="time" value={secondForm.start_time} onChange={(e) => setSecondForm((f) => ({ ...f, start_time: e.target.value }))} className="px-3 py-2 rounded-lg border border-gray-300 text-sm" />
              <input data-testid="quick-assign-second-end-time" required type="time" value={secondForm.end_time} onChange={(e) => setSecondForm((f) => ({ ...f, end_time: e.target.value }))} className="px-3 py-2 rounded-lg border border-gray-300 text-sm" />
            </div>
            <input data-testid="quick-assign-justification" placeholder="Manager justification for the double shift" value={justification} onChange={(e) => setJustification(e.target.value)} className="w-full px-3 py-2 rounded-lg border border-gray-300 text-sm" />
          </>
        )}

        <DialogFooter>
          <button data-testid="quick-assign-cancel" onClick={() => onOpenChange(false)} className="px-4 py-2 rounded-lg border border-gray-300 text-sm font-semibold hover:bg-gray-50">Cancel</button>
          <button data-testid="quick-assign-submit" disabled={submitting} onClick={handleSubmit} className="px-4 py-2 rounded-lg bg-blue-600 text-white text-sm font-semibold hover:bg-blue-700 disabled:opacity-50">
            {submitting ? "Assigning..." : doubleShiftMode ? "Add Double Shift" : "Add Assignment"}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
